import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { subscriptionApi } from '@/entities/subscription/@x/session';
import { ADMIN_ROLES, userApi } from '@/entities/user/@x/session';
import {
  sessionEnded,
  sessionExpired,
  tokensRefreshed,
} from '@/shared/api/sessionEvents';
import type { User } from '@/shared/api/types';

// What "Remember Me" keeps for the next visit: the email only, never the password
export interface RememberedCredentials {
  email?: string;
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isValidRefreshToken: boolean;
  isAdmin: boolean;
  role: number | null;
  onboarding_finished: boolean;
  email_added: boolean;
  rememberMe: RememberedCredentials | null;
  // The session was started with "Remember Me" ticked: it is kept in localStorage and survives
  // closing the browser. Otherwise it lives in sessionStorage and ends with the browser
  // (see `sessionStorageTransform` in app/store). Missing in sessions stored before this flag
  // existed, which are kept as remembered.
  sessionRemembered?: boolean;
  // Why the user was signed out by the backend, shown on the login page
  sessionEndedMessage?: string | null;
  // The code of a sign-up link whose password is not set yet: until it is, the password step is
  // the only page open to that account (AU-4-06). Kept in sessionStorage (see app/store).
  pendingSignUpCode?: string | null;
}

interface AuthCredentials {
  userInfo?: User | null;
  accessToken?: string | null;
  refreshToken?: string | null;
  // Set when a new session starts: was "Remember Me" ticked
  remember?: boolean;
}

interface AuthTokens {
  accessToken?: string | null;
  refreshToken?: string | null;
}

const INITIAL_STATE: AuthState = {
  accessToken: null,
  refreshToken: null,
  isValidRefreshToken: false,
  isAdmin: false,
  role: null,
  onboarding_finished: false,
  email_added: false,
  rememberMe: null,
  sessionRemembered: false,
  pendingSignUpCode: null,
};

const applyAuthCredentials = (
  state: AuthState,
  { userInfo, accessToken, refreshToken, remember }: AuthCredentials,
) => {
  const role = userInfo?.role?.id ?? null;
  const isAdmin = role !== null && ADMIN_ROLES.includes(role);

  if (accessToken !== undefined) {
    state.accessToken = accessToken ?? null;
    // Signed in: the note about the ended session and a waiting sign up are no longer relevant
    if (accessToken) {
      state.sessionEndedMessage = null;
      state.pendingSignUpCode = null;
    }
  }
  if (refreshToken !== undefined) {
    state.refreshToken = refreshToken ?? null;
  }
  if (remember !== undefined) {
    state.sessionRemembered = remember;
  }
  state.isAdmin = isAdmin;
  state.role = role;
  state.onboarding_finished = !!userInfo?.onboarding_finished;
  state.email_added = !!userInfo?.email_added;
};

const authSlice = createSlice({
  name: 'auth',
  initialState: INITIAL_STATE,
  reducers: {
    setAuthCredentials: (state, action: PayloadAction<AuthCredentials>) => {
      applyAuthCredentials(state, action.payload);
    },

    setAuthSocialCreds: (state, action: PayloadAction<AuthCredentials>) => {
      applyAuthCredentials(state, action.payload);
    },

    setTokens: (state, action: PayloadAction<AuthTokens>) => {
      const { accessToken, refreshToken } = action.payload;

      state.accessToken = accessToken ?? null;
      state.refreshToken = refreshToken ?? null;
    },

    setRemember: (
      state,
      action: PayloadAction<RememberedCredentials | null>,
    ) => {
      // Copy the email explicitly so that nothing else from the form gets stored
      state.rememberMe = action.payload
        ? { email: action.payload.email }
        : null;
    },

    setPendingSignUp: (state, action: PayloadAction<string | null>) => {
      state.pendingSignUpCode = action.payload;
    },

    clearState: (state) => ({
      ...INITIAL_STATE,
      rememberMe: state.rememberMe,
    }),

    logout: (state) => ({
      ...INITIAL_STATE,
      rememberMe: state.rememberMe,
    }),

    setValidRefreshToken: (state, action: PayloadAction<boolean>) => {
      state.isValidRefreshToken = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sessionExpired, (state) => ({
        ...INITIAL_STATE,
        rememberMe: state.rememberMe,
      }))
      .addCase(sessionEnded, (state, action) => {
        state.sessionEndedMessage = action.payload;
      })
      .addCase(tokensRefreshed, (state, action) => {
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
      })
      .addMatcher(
        userApi.endpoints.getUserInfo.matchFulfilled,
        (state, action) => {
          applyAuthCredentials(state, { userInfo: action.payload });
        },
      )
      .addMatcher(
        userApi.endpoints.confirmEmails.matchFulfilled,
        (state, action) => {
          applyAuthCredentials(state, {
            userInfo: action.payload.user,
            accessToken: action.payload.access,
            refreshToken: action.payload.refresh,
          });
        },
      )
      .addMatcher(
        subscriptionApi.endpoints.saveSubscription.matchFulfilled,
        (state, action) => {
          if (action.payload?.paid && action.payload.user) {
            applyAuthCredentials(state, { userInfo: action.payload.user });
          }
        },
      );
  },
});

export const {
  setAuthCredentials,
  setAuthSocialCreds,
  setTokens,
  setRemember,
  setPendingSignUp,
  clearState,
  logout,
  setValidRefreshToken,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
